import React from 'react';
import { Card, InfoRow, StatBox, Pill, Logo, EntityLink } from './ui';
import { fmtDate, fixtureInSeason, seasonShortLabel } from '../../lib/playerFormatters';

export function PlayerInfoCard({ player }) {
  return (
    <Card title="Player info">
      <InfoRow label="Full name" value={player?.fullName || player?.name} />
      <InfoRow label="Date of birth" value={fmtDate(player?.dateOfBirth)} />
      <InfoRow label="Age" value={player?.age} />
      <InfoRow label="Nationality" value={player?.nationality?.name} />
      <InfoRow label="Height" value={player?.height} />
      <InfoRow label="Weight" value={player?.weight} />
      <InfoRow label="Preferred foot" value={player?.preferredFoot} />
      <InfoRow label="Gender" value={player?.gender} />
      <InfoRow label="City" value={player?.city} />
      <InfoRow label="Common name" value={player?.commonName} />
      <InfoRow label="Position" value={player?.detailedPosition || player?.position} />
    </Card>
  );
}

export function RecentStatsCard({ player, season }) {
  const s = season || player?.currentSeasonStats || {};
  return (
    <Card title={season?.seasonName || 'Current season'}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <StatBox label="Apps" value={s.appearances} />
        <StatBox label="Goals" value={s.goals} />
        <StatBox label="Assists" value={s.assists} />
        <StatBox label="Minutes" value={s.minutes} />
        <StatBox label="Rating" value={s.rating} />
        <StatBox label="Passes" value={s.passes} />
        <StatBox label="Shots" value={s.shotsTotal} />
        <StatBox label="Tackles" value={s.tackles} />
      </div>
    </Card>
  );
}

export function HonoursCard({ player, onFull }) {
  const t = player?.trophies || {};
  return (
    <Card title="Honours" action={onFull && <button className="i-back" onClick={onFull}>Full →</button>}>
      <div className="flex gap-2 mb-3">
        <Pill className="i-pos-def">Wins {t.wins || 0}</Pill>
        <Pill className="i-bd">Runner-up {t.runnerUp || 0}</Pill>
      </div>
      {(t.list || []).slice(0, 5).map((tr, i) => (
        <div key={i} className="i-row">
          <span className="i-mute">{tr.season || '—'}</span>
          <span className="i-fg text-right">
            <EntityLink to={tr.leagueId ? `/league/${tr.leagueId}` : null}>{tr.league || '—'}</EntityLink>
            {' '}
            <Pill className={tr.result === 'Winner' ? 'i-pos-def' : 'i-bd'}>{tr.result}</Pill>
          </span>
        </div>
      ))}
      {!(t.list || []).length && <p className="i-dim text-xs">No trophies recorded.</p>}
    </Card>
  );
}

export function CurrentClubCard({ player }) {
  const c = player?.currentClub || {};
  return (
    <Card title="Current club">
      <div className="flex items-center gap-3 mb-3">
        <EntityLink to={c.id ? `/club/${c.id}` : null}>
          <Logo src={c.image} alt={c.name} size={40} fallback={c.name} />
        </EntityLink>
        <EntityLink to={c.id ? `/club/${c.id}` : null} className="i-fg font-semibold">{c.name || '—'}</EntityLink>
      </div>
      <InfoRow label="Founded" value={c.founded} />
      <InfoRow label="Jersey no." value={c.jerseyNumber} />
      <InfoRow label="Captain" value={c.captain ? 'Yes' : 'No'} />
      <InfoRow label="Joined" value={fmtDate(c.joined)} />
      <InfoRow label="Contract until" value={fmtDate(c.contractUntil)} />
    </Card>
  );
}

export function TransferHistoryCard({ player, onFull }) {
  const list = player?.transfers || [];
  return (
    <Card title="Transfer history" action={onFull && <button className="i-back" onClick={onFull}>Full →</button>}>
      {(list.length ? list.slice(0, 4) : []).map((t, i) => (
        <div key={i} className="i-row">
          <span className="i-mute">{fmtDate(t.date)}</span>
          <span className="i-fg text-right text-sm">
            <EntityLink to={t.from?.id ? `/club/${t.from.id}` : null}>{t.from?.name || '—'}</EntityLink>
            {' → '}
            <EntityLink to={t.to?.id ? `/club/${t.to.id}` : null}>{t.to?.name || '—'}</EntityLink>
            <div className="i-dim text-xs">{t.feeLabel || t.type || '—'}</div>
          </span>
        </div>
      ))}
      {!list.length && <p className="i-dim text-xs">No transfers recorded.</p>}
    </Card>
  );
}

export function RecentMatchesCard({ player, season }) {
  const all = player?.recentMatches || [];
  const list = season ? all.filter((m) => fixtureInSeason(m.date, season)) : all;
  return (
    <Card title="Recent matches">
      {(list.length ? list.slice(0, 5) : []).map((m, i) => (
        <div key={i} className="i-row">
          <span className="i-mute text-xs">{fmtDate(m.date) || '—'}</span>
          <span className="i-fg flex items-center gap-1.5 text-sm justify-end text-right">
            <Logo src={m.home?.image} alt={m.home?.name} size={16} fallback={m.home?.name} />
            <span>{m.home?.name || '—'}</span>
            <span className="i-dim">{m.score || 'v'}</span>
            <span>{m.away?.name || '—'}</span>
            <Logo src={m.away?.image} alt={m.away?.name} size={16} fallback={m.away?.name} />
          </span>
        </div>
      ))}
      {!list.length && <p className="i-dim text-xs">No recent matches.</p>}
    </Card>
  );
}

export function MatchAppearancesTable({ player, season }) {
  const all = player?.recentMatches || [];
  const list = season ? all.filter((m) => fixtureInSeason(m.date, season)) : all;
  if (!list.length) return <p className="i-dim text-sm">No match appearances for {season ? seasonShortLabel(season) : 'this season'}.</p>;
  return (
    <Card title="Match appearances" bodyClass="p-0">
      <div className="overflow-x-auto">
        <table className="i-table min-w-[760px]">
          <thead>
            <tr><th>Date</th><th>Competition</th><th>Matchup</th><th>Role</th><th>No.</th></tr>
          </thead>
          <tbody>
            {list.map((m, i) => (
              <tr key={i}>
                <td>{fmtDate(m.date) || '—'}</td>
                <td>{m.competition || '—'}</td>
                <td>
                  <span className="flex items-center gap-1.5">
                    <Logo src={m.home?.image} alt={m.home?.name} size={18} fallback={m.home?.name} />
                    <EntityLink to={m.home?.id ? `/club/${m.home.id}` : null} className="i-fg">{m.home?.name || '—'}</EntityLink>
                    <span className="i-dim">{m.score || 'v'}</span>
                    <EntityLink to={m.away?.id ? `/club/${m.away.id}` : null} className="i-fg">{m.away?.name || '—'}</EntityLink>
                    <Logo src={m.away?.image} alt={m.away?.name} size={18} fallback={m.away?.name} />
                  </span>
                </td>
                <td>{m.role}</td>
                <td>{m.jersey != null ? `#${m.jersey}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function CareerTotalsCard({ player }) {
  const c = player?.career || {};
  return (
    <Card title="Career totals">
      <div className="grid grid-cols-3 gap-3">
        <StatBox label="Apps" value={c.appearances} />
        <StatBox label="Goals" value={c.goals} />
        <StatBox label="Assists" value={c.assists} />
        <StatBox label="Minutes" value={c.minutesPlayed} />
        <StatBox label="Avg rating" value={c.averageRating} />
        <StatBox label="Goals/90" value={c.goalsPer90} />
      </div>
    </Card>
  );
}

export function SquadInfoCard({ player }) {
  const c = player?.currentClub || {};
  return (
    <Card title="Squad info">
      <InfoRow label="Club" value={c.name} />
      <InfoRow label="Position" value={player?.position} />
      <InfoRow label="Jersey no." value={c.jerseyNumber} />
      <InfoRow label="Captain" value={c.captain ? 'Yes' : 'No'} />
      <InfoRow label="Joined" value={fmtDate(c.joined)} />
    </Card>
  );
}

export function SeasonStatDetail({ season }) {
  if (!season) return <p className="i-dim text-sm">No season data available.</p>;
  return (
    <Card title={season.seasonName || 'Current season'}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Apps" value={season.appearances} />
        <StatBox label="Lineups" value={season.lineups} />
        <StatBox label="Bench" value={season.bench} />
        <StatBox label="Minutes" value={season.minutes} />
        <StatBox label="Goals" value={season.goals} />
        <StatBox label="Assists" value={season.assists} />
        <StatBox label="Rating" value={season.rating} />
        <StatBox label="Yellow" value={season.yellowCards} />
        <StatBox label="Shots" value={season.shotsTotal} />
        <StatBox label="On target" value={season.shotsOnTarget} />
        <StatBox label="Passes" value={season.passes} />
        <StatBox label="Pass acc %" value={season.passAccuracyPct} />
        <StatBox label="Tackles" value={season.tackles} />
        <StatBox label="Interceptions" value={season.interceptions} />
        <StatBox label="Duels won" value={season.duelsWon} />
        <StatBox label="Total duels" value={season.duelsTotal} />
        <StatBox label="Crosses" value={season.crosses} />
        <StatBox label="Dribbles" value={season.dribbleAttempts} />
        <StatBox label="Clean sheets" value={season.cleanSheets} />
        <StatBox label="Team W-D-L" value={[season.teamWins, season.teamDraws, season.teamLost].filter(v => v != null).length ? `${season.teamWins ?? 0}-${season.teamDraws ?? 0}-${season.teamLost ?? 0}` : null} />
        <StatBox label="Goals/90" value={season.goalsPer90} />
        <StatBox label="Assists/90" value={season.assistsPer90} />
      </div>
    </Card>
  );
}

export function SeasonStatsTable({ seasons, highlightId }) {
  const list = seasons || [];
  if (!list.length) return <p className="i-dim text-sm">No season history.</p>;
  return (
    <Card title="Season history" bodyClass="p-0">
      <div className="overflow-x-auto">
        <table className="i-table min-w-[760px]">
          <thead>
            <tr><th>Season</th><th>Team</th><th>League</th><th>Apps</th><th>Goals</th><th>Assists</th><th>Mins</th><th>Rating</th><th>P. acc %</th></tr>
          </thead>
          <tbody>
            {list.map((s, i) => (
              <tr key={i} style={highlightId != null && String(s.seasonId) === String(highlightId) ? { background: 'rgba(154,170,184,0.08)' } : undefined}>
                <td>{s.seasonLabel || s.seasonName}{s.isCurrent ? ' ●' : ''}</td>
                <td>{s.teamName || '—'}</td>
                <td>{s.leagueName || '—'}</td>
                <td>{s.appearances ?? '—'}</td>
                <td>{s.goals ?? '—'}</td>
                <td>{s.assists ?? '—'}</td>
                <td>{s.minutes ?? '—'}</td>
                <td>{s.rating ?? '—'}</td>
                <td>{s.passAccuracyPct ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
